import { useEffect, useState } from "react";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/temple/Nav";
import Footer from "@/components/temple/Footer";
import Home from "@/pages/Home";
import GatePage from "@/pages/GatePage";
import Inbox from "@/pages/Inbox";
import Gift, { GiftRedeem } from "@/pages/Gift";
import { MotionConfig } from "framer-motion";
import { applyCalm, isCalm } from "@/lib/calm";
import Journey from "@/pages/Journey";
import Reminders from "@/pages/Reminders";
import ReviewReflection from "@/pages/ReviewReflection";
import Blessings from "@/pages/Blessings";
import MoonRitual from "@/components/temple/MoonRitual";
import MoonGlow from "@/components/temple/MoonGlow";
import Initiate from "@/pages/Initiate";
import TempleApp from "@/pages/TempleApp";
import QuantumApp from "@/pages/QuantumApp";
import Apps from "@/pages/Apps";
import { SignIn, ResetPassword, PaymentSuccess, PaymentCancel } from "@/pages/Account";
import { AuthProvider } from "@/lib/auth";

const RouteScroll = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.__lenis ? window.__lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const Shell = () => {
  const { pathname } = useLocation();
  const isInbox = pathname.startsWith("/inbox");
  const inApp = pathname === "/app" || pathname.startsWith("/app/");
  const inQuantum = pathname === "/quantum" || pathname.startsWith("/quantum/");
  const bare = isInbox || inApp || inQuantum;
  return (
    <>
      <RouteScroll />
      {!bare && <MoonGlow />}
      {!bare && <Nav />}
      <Routes>
        <Route path="/app/*" element={<TempleApp />} />
        <Route path="/quantum/*" element={<QuantumApp />} />
        <Route path="/apps" element={<Apps />} />
        <Route path="/" element={<Home />} />
        <Route path="/gates/:slug" element={<GatePage />} />
        <Route path="/inbox" element={<Inbox />} />
        <Route path="/journey" element={<Journey />} />
        <Route path="/reminders" element={<Reminders />} />
        <Route path="/review-reflection" element={<ReviewReflection />} />
        <Route path="/blessings" element={<Blessings />} />
        <Route path="/initiate" element={<Initiate />} />
        <Route path="/gift" element={<Gift />} />
        <Route path="/gift/redeem" element={<GiftRedeem />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="*" element={<Home />} />
      </Routes>
      {!bare && <Footer />}
      {!isInbox && !inQuantum && <MoonRitual />}
    </>
  );
};

function App() {
  const [ready, setReady] = useState(false);
  const [calm, setCalm] = useState(isCalm);
  useEffect(() => {
    applyCalm();
    const on = () => setCalm(isCalm());
    window.addEventListener("tt-calm", on);
    return () => window.removeEventListener("tt-calm", on);
  }, []);
  useEffect(() => {
    if (calm) {
      setReady(true);
      return undefined;
    }
    const lenis = new Lenis({ lerp: 0.085 });
    window.__lenis = lenis;
    setReady(true);
    let id;
    const raf = (t) => {
      lenis.raf(t);
      id = requestAnimationFrame(raf);
    };
    id = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
      window.__lenis = undefined;
    };
  }, [calm]);

  return (
    <MotionConfig reducedMotion={calm ? "always" : "user"}>
    <div className="App grain" data-lenis-ready={ready}>
      <AuthProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </AuthProvider>
      <Toaster theme="dark" position="bottom-right" />
    </div>
    </MotionConfig>
  );
}

export default App;
